import React, { useState, useMemo, useCallback } from 'react';
import cn from 'classnames';
import { CopilotLineGraph } from 'apps/sigma2-ui/src/copilot/components/CopilotLineGraph';
import { CopilotLineGraphPerformanceBar } from 'apps/sigma2-ui/src/copilot/components/CopilotLineGraphPerformanceBar';
import { CopilotLineGraphTickLabelsView } from 'apps/sigma2-ui/src/copilot/components/CopilotLineGraphTickLabelsView';
import { TooltipPosition } from 'apps/sigma2-ui/src/copilot/components/CopilotLineGraphTooltipPositioning';
import { CopilotLineGraphTooltipView } from 'apps/sigma2-ui/src/copilot/components/CopilotLineGraphTooltipView';
import { ReadinessRatingTooltip } from 'apps/sigma2-ui/src/copilot/components/ReadinessRatingTooltip';
import { ReadinessRatingView } from 'apps/sigma2-ui/src/copilot/components/ReadinessRatingView';
import { CopilotViewFilters } from 'apps/sigma2-ui/src/copilot/components/CopilotViewFilters';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { FocusArea } from 'libs/end-to-end/src/focus-area';
import { formatPriceAmountToString } from 'libs/end-to-end/src/currencies';
import { Alert } from '@mui/material';
import { SimpleSlider } from 'apps/sigma2-ui/src/components/ui/SimpleSlider';
import { FilterableTableHeaderCell } from 'apps/sigma2-ui/src/components/ui/Table';
import { SortableTableHeaderCell } from 'apps/sigma2-ui/src/components/ui/Table';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { TablePaginationFooter } from 'apps/sigma2-ui/src/components/ui/Table';
import { TABLE_HEADER_OFFSET_HEIGHT } from 'apps/sigma2-ui/src/components/ui/Table';
import { IconAddCircleOutlineSvg } from 'apps/sigma2-ui/src/components/svg/IconAddCircleOutline';
import { MinusCircleIcon } from '@heroicons/react/20/solid';
import { useTableSorting } from 'apps/sigma2-ui/src/lib/hooks/useTableSorting';
import { useCheckboxGroup } from 'apps/sigma2-ui/src/components/ui/CheckboxGroup';
import { Pill } from 'apps/sigma2-ui/src/components/Pill';
import { Over40IconSvg } from 'apps/sigma2-ui/src/components/svg/Over40Icon';
import { Under40IconSvg } from 'apps/sigma2-ui/src/components/svg/Under40Icon';
import { PillBorder } from 'apps/sigma2-ui/src/components/PillBorder';
import { useInfiniteScrollPagination } from 'apps/sigma2-ui/src/lib/hooks/useInfiniteScrollPagination';
import { useIntersectionObserver } from 'apps/sigma2-ui/src/lib/hooks/useIntersectionObserver';
import { Paragraph } from 'apps/sigma2-ui/src/components/ui/Paragraph';
import { createClusters } from 'libs/end-to-end/src/copilot-utils';
import { SlateCopilotDataPoint } from 'libs/end-to-end/src/copilot';
import { SlatePoolSubject } from 'libs/end-to-end/src/copilot';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { LevelDefinitionButton } from 'apps/sigma2-ui/src/components/LevelDefinitionButton';
import { extractFirstCopilotDataPointIdForReadiness } from 'libs/end-to-end/src/copilot-utils';
import { getSelectedClusteredDataPoint } from 'libs/end-to-end/src/copilot-utils';
import { getSubjectsForClusteredDataPoint } from 'libs/end-to-end/src/copilot-utils';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { DataRefreshIndicator } from 'apps/sigma2-ui/src/components/custom-viz/DataRefreshIndicator';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';

const PoolHeading = ({
  focusArea,
  isTable,
}: {
  focusArea: FocusArea;
  isTable: boolean;
}) => {
  const language = useLanguage();
  return (
    <div className="relative">
      <div className="pb-6">
        <Paragraph textColor="text-gray-600">
          {focusArea === 'Hiring'
            ? isTable
              ? language.hiringCopilotTableDescription()
              : language.hiringCopilotChartDescription()
            : isTable
              ? language.promotionCopilotTableDescription()
              : language.promotionCopilotChartDescription()}
        </Paragraph>
      </div>
    </div>
  );
};

interface CopilotPoolViewProps {
  dataPoints: SlateCopilotDataPoint[];
  allApplicants: SlatePoolSubject[];
  setShowTooltip: (showTooltip: boolean) => void;
  showTooltip: boolean;
  onAddToSlate: (subject: SlatePoolSubject) => void;
  onRemoveFromSlate: (subject: SlatePoolSubject) => void;
  selectedSubject: SlatePoolSubject | null;
  handleClickOnGraph: (subjectId: string | null) => void;

  showError: boolean;

  onApplyFilters: () => void;
  canApplyFilters: boolean;

  departments: string[];
  roles: string[];
  locations: string[];
  selectedDepartments: string[];
  selectedRoles: string[];
  selectedLocations: string[];
  setSelectedDepartments: (value: string[]) => void;
  setSelectedRoles: (value: string[]) => void;
  setSelectedLocations: (value: string[]) => void;
  selectedStartDate?: Date;
  selectedEndDate?: Date;
  setSelectedStartDate: (value?: Date) => void;
  setSelectedEndDate: (value?: Date) => void;
  focusArea: FocusArea;
  customVizStatus: CustomVizHookStatus;
  customVizResultCreatedAt: number | null;
}

type CopilotPoolDisplayType = 'Chart' | 'List';

// eslint-disable-next-line complexity
export const CopilotPoolView = (props: CopilotPoolViewProps) => {
  const language = useLanguage();
  const [tooltipPosition, setTooltipPosition] =
    useState<TooltipPosition | null>('middle_left');
  const onTapCloseTooltip = () => {
    props.setShowTooltip(false);
  };

  const { allLevels } = useDimensionConfig();

  const onClickLineGraphPoint = (
    tooltipPosition: TooltipPosition,
    minimumReadiness: number,
    maximumReadiness: number,
  ) => {
    const firstId = extractFirstCopilotDataPointIdForReadiness({
      dataPoints: props.dataPoints,
      maximumReadiness,
      minimumReadiness,
      sortType: 'highest-to-lowest',
    });

    props.handleClickOnGraph(firstId ?? null);
    setTooltipPosition(tooltipPosition);
  };

  const selectedSubject = useMemo(() => {
    return props.selectedSubject;
  }, [props.selectedSubject]);

  const clusteredDataPoints = useMemo(() => {
    return createClusters(props.dataPoints, 3);
  }, [props.dataPoints]);

  const selectedClusteredDataPoint = useMemo(() => {
    return getSelectedClusteredDataPoint({
      clusteredDataPoints,
      selectedSubject,
    });
  }, [clusteredDataPoints, selectedSubject]);

  const displayedSubjectsInSelectedCluster = useMemo(() => {
    return getSubjectsForClusteredDataPoint({
      clusteredDataPoint: selectedClusteredDataPoint ?? null,
      dataPoints: props.dataPoints,
      sortType: 'highest-to-lowest',
    });
  }, [props.dataPoints, selectedClusteredDataPoint]);

  const canGoLeft = useMemo(() => {
    const index = displayedSubjectsInSelectedCluster.findIndex(
      (subject) => subject.id === selectedSubject?.id,
    );
    return index > 0;
  }, [displayedSubjectsInSelectedCluster, selectedSubject]);

  const canGoRight = useMemo(() => {
    const index = displayedSubjectsInSelectedCluster.findIndex(
      (subject) => subject.id === selectedSubject?.id,
    );
    return index + 1 < displayedSubjectsInSelectedCluster.length;
  }, [displayedSubjectsInSelectedCluster, selectedSubject]);

  const onGoLeft = useCallback(() => {
    if (!canGoLeft) {
      return;
    }
    const index = displayedSubjectsInSelectedCluster.findIndex(
      (subject) => subject.id === selectedSubject?.id,
    );
    const nextSubject = displayedSubjectsInSelectedCluster.at(index - 1);
    if (nextSubject) {
      props.handleClickOnGraph(nextSubject.id);
    }
  }, [canGoLeft, displayedSubjectsInSelectedCluster, props, selectedSubject]);

  const onGoRight = useCallback(() => {
    if (!canGoRight) {
      return;
    }
    const index = displayedSubjectsInSelectedCluster.findIndex(
      (subject) => subject.id === selectedSubject?.id,
    );
    const nextSubject = displayedSubjectsInSelectedCluster.at(index + 1);
    if (nextSubject) {
      props.handleClickOnGraph(nextSubject.id);
    }
  }, [canGoRight, displayedSubjectsInSelectedCluster, props, selectedSubject]);

  const [hoverOverReadinessRating, setHoverOverReadinessRating] =
    useState(false);

  const [currentDisplayType, setCurrentDisplayType] =
    useState<CopilotPoolDisplayType>('Chart');

  const { onSortChange, sortedItems, getSortingInfo } = useTableSorting({
    initialSortedTableColumns: [
      { id: 'readinessRating', direction: 'desc' },
      { id: 'sortableTotalCompensation', direction: 'desc' },
    ],
    items: props.allApplicants,
  });

  const {
    checkboxes,
    selectAllCheckbox,
    onChange,
    onSelectAllChange,
    getCheckboxInfo,
  } = useCheckboxGroup({
    initialSelectAllCheckbox: {
      checked: true,
      children: 'Select all',
    },
    initialCheckboxes: allLevels.map((levelDefinition) => {
      return {
        id: levelDefinition.columnValue,
        children: levelDefinition.displayName,
        checked: true,
      };
    }),
  });

  const { paginatedItems, onIntersect, doneIntersecting } =
    useInfiniteScrollPagination({
      limit: 10,
      items: sortedItems,
    });

  const [
    lastRowRef,
    { rootRef: tableContainerRef, isIntersecting: isLoadingMore },
  ] = useIntersectionObserver<HTMLTableRowElement, HTMLDivElement>({
    onIntersectSync: onIntersect,
    options: { rootMargin: TABLE_HEADER_OFFSET_HEIGHT },
  });

  const tableView = useMemo(() => {
    return (
      <Table
        refs={{ lastRow: lastRowRef, tableContainer: tableContainerRef }}
        items={paginatedItems}
        classNames={{
          tableContainer: 'h-full max-h-[50vh] overflow-y-scroll',
          outerContainer: 'h-full',
        }}
        isLoading={isLoadingMore && !doneIntersecting}
        footer={
          sortedItems.length > 0 && (
            <TablePaginationFooter
              numTotalItems={sortedItems.length}
              numVisibleItems={paginatedItems.length}
            />
          )
        }
        columnDefinitions={[
          {
            id: 'id',
            header: () => <div>Id</div>,
            cell: (params: { item: SlatePoolSubject }) => (
              <div>{params.item.id}</div>
            ),
          },
          {
            id: 'dimension',
            header: () => (
              <FilterableTableHeaderCell
                selectAllCheckbox={selectAllCheckbox}
                checkboxes={checkboxes}
                onSelectAllChange={onSelectAllChange}
                onChange={onChange}
              >
                Dimensions
              </FilterableTableHeaderCell>
            ),
            cell: (params: { item: SlatePoolSubject }) => {
              const visibleLevelDefinition =
                params.item.levelDefinitions.filter((levelDefinition) => {
                  return getCheckboxInfo({ id: levelDefinition.columnValue })
                    ?.isChecked;
                });

              if (visibleLevelDefinition.length === 0) {
                return <p>-</p>;
              }

              return (
                <div className="flex flex-wrap gap-2">
                  {visibleLevelDefinition.map((levelDefinition) => {
                    return (
                      <LevelDefinitionButton
                        key={levelDefinition.columnValue}
                        disabled={true}
                        selected={false}
                        levelDefinition={levelDefinition}
                        icon={true}
                      />
                    );
                  })}
                  <PillBorder>
                    <Pill
                      bgClassName="bg-transparent"
                      icon={
                        params.item.isOver40 ? (
                          <Over40IconSvg />
                        ) : (
                          <Under40IconSvg />
                        )
                      }
                    >
                      <S2Typography
                        variant="body-7"
                        translatedContent={
                          params.item.isOver40
                            ? language.hiringCopilotOver40()
                            : language.hiringCopilotUnder40()
                        }
                        textColorVariant="gray-1000"
                      />
                    </Pill>
                  </PillBorder>
                </div>
              );
            },
          },
          {
            id: 'base_pay',
            header: () => <div>Base Pay</div>,
            cell: (params: { item: SlatePoolSubject }) => (
              <div>
                {params.item.compensationFigures && (
                  <div>
                    {formatPriceAmountToString({
                      amount:
                        params.item.compensationFigures.suggestedBaseSalary,
                      currency: 'USD',
                      roundToWholeValue: true,
                    })}
                    /yr
                  </div>
                )}
              </div>
            ),
          },
          {
            id: 'variable_pay',
            header: () => <div>Variable Pay</div>,
            cell: (params: { item: SlatePoolSubject }) => (
              <div>
                {params.item.compensationFigures && (
                  <div>
                    {formatPriceAmountToString({
                      amount:
                        params.item.compensationFigures.suggestedVariablePay,
                      currency: 'USD',
                      roundToWholeValue: true,
                    })}
                    /yr
                  </div>
                )}
              </div>
            ),
          },
          {
            id: 'sortableTotalCompensation',
            header: () => (
              <SortableTableHeaderCell
                id="sortableTotalCompensation"
                {...getSortingInfo({ id: 'sortableTotalCompensation' })}
                onSortChange={onSortChange}
              >
                Total Comp.
              </SortableTableHeaderCell>
            ),
            cell: (params: { item: SlatePoolSubject }) => (
              <div>
                {params.item.compensationFigures && (
                  <div>
                    {formatPriceAmountToString({
                      amount:
                        params.item.compensationFigures
                          .suggestedTotalCompensation,
                      currency: 'USD',
                      roundToWholeValue: true,
                    })}
                    /yr
                  </div>
                )}
              </div>
            ),
          },
          {
            id: 'readinessRating',
            header: () => (
              <SortableTableHeaderCell
                id="readinessRating"
                {...getSortingInfo({ id: 'readinessRating' })}
                onSortChange={onSortChange}
              >
                Readiness
              </SortableTableHeaderCell>
            ),
            cell: (params: { item: SlatePoolSubject }) => {
              const textColor =
                params.item.confidenceLevel === 'high'
                  ? 'text-green-600'
                  : params.item.confidenceLevel === 'low'
                    ? 'text-red-600'
                    : 'text-black';
              return (
                <Paragraph
                  textColor={textColor}
                >{`${params.item.readinessRating}%`}</Paragraph>
              );
            },
          },
          {
            id: 'slate',
            header: () => <div>Slate</div>,
            cell: (params: { item: SlatePoolSubject }) => (
              <div>
                <button
                  onClick={() => {
                    if (params.item.isInSlate) {
                      props.onRemoveFromSlate(params.item);
                    } else {
                      props.onAddToSlate(params.item);
                    }
                  }}
                >
                  {!params.item.isInSlate ? (
                    <IconAddCircleOutlineSvg
                      svg={{
                        className: 'text-tertiary',
                        height: 24,
                        width: 24,
                      }}
                    />
                  ) : (
                    <MinusCircleIcon className="w-6 h-6 text-tertiary" />
                  )}
                </button>
              </div>
            ),
          },
        ]}
      />
    );
  }, [
    lastRowRef,
    tableContainerRef,
    paginatedItems,
    isLoadingMore,
    doneIntersecting,
    sortedItems.length,
    selectAllCheckbox,
    checkboxes,
    onSelectAllChange,
    onChange,
    language,
    getCheckboxInfo,
    getSortingInfo,
    onSortChange,
    props,
  ]);

  return (
    <>
      <div className="flex flex-col gap-3 relative">
        <div className="flex items-center gap-2">
          <Paragraph textColor="text-black" fontFamily="font-sans">
            View as:
          </Paragraph>
          <SimpleSlider
            currentValue={currentDisplayType}
            selectableValues={['Chart', 'List']}
            onChange={(value) =>
              setCurrentDisplayType(value as CopilotPoolDisplayType)
            }
          />
        </div>
        <DataRefreshIndicator
          status={props.customVizStatus}
          resultCreatedAt={props.customVizResultCreatedAt}
          heightClass={'h-auto'}
        />
        <CopilotViewFilters
          departments={props.departments}
          roles={props.roles}
          locations={props.locations}
          selectedDepartments={props.selectedDepartments}
          selectedRoles={props.selectedRoles}
          selectedLocations={props.selectedLocations}
          setSelectedDepartments={props.setSelectedDepartments}
          setSelectedRoles={props.setSelectedRoles}
          setSelectedLocations={props.setSelectedLocations}
          selectedStartDate={props.selectedStartDate}
          selectedEndDate={props.selectedEndDate}
          setSelectedStartDate={props.setSelectedStartDate}
          setSelectedEndDate={props.setSelectedEndDate}
          canApplyFilters={props.canApplyFilters}
          onApplyFilters={props.onApplyFilters}
          focusArea={props.focusArea}
        />
        <PoolHeading
          focusArea={props.focusArea}
          isTable={currentDisplayType === 'List'}
        />
        <div className="relative">
          <ReadinessRatingTooltip
            show={hoverOverReadinessRating}
            copilotType={props.focusArea === 'Hiring' ? 'hiring' : 'promotion'}
          />
          {currentDisplayType === 'List' && (
            <div className={cn('relative h-[40rem] rounded-t')}>
              {tableView}
            </div>
          )}
          {currentDisplayType === 'Chart' && (
            <>
              <div
                className={cn(
                  'relative h-[480px] w-[865px] rounded-t border-b border-blue-800',
                )}
              >
                {props.showTooltip && selectedSubject && tooltipPosition && (
                  <CopilotLineGraphTooltipView
                    subject={selectedSubject}
                    onClose={onTapCloseTooltip}
                    tooltipPosition={tooltipPosition}
                    onAddToSlate={() => {
                      if (!selectedSubject) {
                        return;
                      }

                      props.onAddToSlate(selectedSubject);
                    }}
                    onRemoveFromSlate={() => {
                      if (!selectedSubject) {
                        return;
                      }

                      props.onRemoveFromSlate(selectedSubject);
                    }}
                    onGoLeft={onGoLeft}
                    onGoRight={onGoRight}
                    isLeftDisabled={!canGoLeft}
                    isRightDisabled={!canGoRight}
                  />
                )}
                <CopilotLineGraph
                  onClick={onClickLineGraphPoint}
                  dataPoints={props.dataPoints}
                  clusteredDataPoints={clusteredDataPoints}
                  currentlySelectedSubject={selectedSubject ?? null}
                >
                  {props.showError && (
                    <div className="flex items-center justify-center w-full h-full pb-10 bg-gray-100 bg-opacity-80 pr-7">
                      <Alert severity="error">
                        {props.focusArea === 'Hiring'
                          ? language.hiringCopilotErrorMessage()
                          : language.promotionCopilotErrorMessage()}
                      </Alert>
                    </div>
                  )}
                </CopilotLineGraph>
              </div>
              <div className={'w-[865px]'}>
                <CopilotLineGraphPerformanceBar />
                <CopilotLineGraphTickLabelsView />
              </div>
            </>
          )}
        </div>
      </div>
      {currentDisplayType === 'Chart' && (
        <ReadinessRatingView
          setHoverOverReadinessRating={setHoverOverReadinessRating}
        />
      )}
      {props.allApplicants.length === 0 && (
        <div className="top-1/2 left-1/2 transform -translate-x-1/2 absolute z-5 font-sans">
          <div className="flex rounded-lg border-white bg-blue-100 justify-center items-center w-[400px]">
            <div className="p-4">
              <div className="py-1 text-gray-900 font-bold">
                {props.focusArea === 'Hiring'
                  ? language.hiringCopilotEmptyStateTitle()
                  : language.promotionCopilotEmptyStateTitle()}
              </div>
              <div className="text-gray-800">
                {props.focusArea === 'Hiring'
                  ? language.hiringCopilotEmptyStateDescription()
                  : language.promotionCopilotEmptyStateDescription()}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
